// imagepasteUrls — re-sign expired `whiteboard.imagepaste.url` presigned GETs
// by basename, via the dispatcher's owner-only
// `POST /rooms/:roomId/imagepaste-urls` (vandalism-recovery-ui-2026-09-27.md
// 「タイムラプスの imagepaste 再生として先行実装する」節, fable-advisor
// レビュー済み).
//
// The broadcast `whiteboard.imagepaste.url` a stroke-log segment carries is a
// presigned GET that expires ~30 min after the original paste
// (`IMAGEPASTE_GET_TTL_SECONDS`) — replaying an old segment needs a fresh
// signature. This endpoint sits right next to `GET /rooms/:roomId/stroke-log`
// (`strokeLogTimeline.ts`) and shares its authz level (Bearer admin_token,
// owner only) and dispatcher-base-URL convention — see that module's header
// for why this doesn't extend `DispatcherClient`.
//
// Server does NOT accept URLs, only basenames (`{uploadId}.{ext}`, the last
// path segment of the presigned GET, no query) — the route rebuilds the R2
// key from the room's own `:roomId` param, so it structurally cannot sign
// another room's images. We hand-roll the same shape here (not imported from
// `@oekaki/server`) — same "web defines its own mirror" convention
// `strokeLogTimeline.ts` uses for `StrokeLogListResponse`.

import type { Logger } from '../../application/ports/Logger.js';

/**
 * Mirrors the server's `IMAGEPASTE_URLS_MAX_NAMES`
 * (`createDispatcherApp.ts`) — a request over this many names gets a 400
 * (server does not silently truncate), so the client must split.
 */
export const IMAGEPASTE_URLS_BATCH_SIZE = 200;

/** Mirrors the server's basename validation (`IMAGEPASTE_BASENAME_RE`). Used
 * client-side only as a pre-filter so obviously-malformed basenames (a
 * `parseImagepasteBasename` bug, or a hand-built fixture in tests) don't
 * even leave the client — the server re-validates independently regardless. */
export const IMAGEPASTE_BASENAME_RE = /^[0-9a-f-]{36}\.(png|jpe?g|gif)$/;

/** Mirrors the server's `ImagepasteUrlsResponse` (`createDispatcherApp.ts`). */
export interface ImagepasteUrlsResponse {
  readonly urls: Record<string, string | null>;
  readonly urlExpiresInSeconds: number;
}

/** Narrow fetch shape so tests inject a stub (same convention as `strokeLogTimeline`). */
export type FetchFn = (
  input: string,
  init?: {
    readonly method?: string;
    readonly signal?: AbortSignal;
    readonly headers?: Record<string, string>;
    readonly body?: string;
  },
) => Promise<Response>;

const defaultFetchFn: FetchFn = (input, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

const noopLogger: Logger = {
  debug: () => undefined,
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

/**
 * Extract the basename (`{uploadId}.{ext}`) from a `whiteboard.imagepaste.url`
 * presigned GET — the last path segment, query string stripped. Broadcast
 * events carry no separate `uploadId` field, so this is the only way to
 * recover it (module header §「タイムラプスの imagepaste 再生」).
 *
 * Returns `null` for anything that doesn't parse as a URL with a non-empty
 * last path segment (defensive — a malformed/legacy `url` degrades to "no
 * basename to re-sign", not a thrown error).
 */
export const parseImagepasteBasename = (url: string): string | null => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const segments = parsed.pathname.split('/').filter((s) => s.length > 0);
  const last = segments[segments.length - 1];
  return last !== undefined && last.length > 0 ? last : null;
};

export interface ResignImagepasteUrlsInput {
  /** Dispatcher base URL (`readEnv().dispatcherUrl`). Trailing `/` tolerated. */
  readonly dispatcherUrl: string;
  readonly roomId: string;
  /** Basenames to re-sign. Deduplicated internally; order is not preserved in the result (a `Map`). */
  readonly names: readonly string[];
  /** Owner `admin_token` — required (callers gate on its presence before calling this at all, §「admin token が無いときは endpoint を呼ばない」). */
  readonly adminToken: string;
  readonly signal?: AbortSignal;
}

export interface ResignImagepasteUrlsDeps {
  readonly fetchFn?: FetchFn;
  readonly logger?: Logger;
}

/**
 * Re-sign a set of basenames, splitting into {@link IMAGEPASTE_URLS_BATCH_SIZE}
 * -sized POSTs (the server 400s over the cap — 「超過分はクライアントが分割
 * して送る」). A batch that fails outright (network error / non-200 / bad
 * JSON body) resolves every one of THAT batch's names to `null` rather than
 * throwing — a resign failure degrades to "try the original url once" per
 * event (see `imagepasteResolve.ts`), it never aborts the whole replay.
 *
 * Names failing {@link IMAGEPASTE_BASENAME_RE} are dropped before any
 * network call (resolve to `null` directly) — the server would reject them
 * too, no point spending a request slot on them.
 */
export const resignImagepasteUrls = async (
  input: ResignImagepasteUrlsInput,
  deps: ResignImagepasteUrlsDeps = {},
): Promise<Map<string, string | null>> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const logger = deps.logger ?? noopLogger;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/rooms/${encodeURIComponent(input.roomId)}/imagepaste-urls`;

  const out = new Map<string, string | null>();
  const toSign: string[] = [];
  for (const name of new Set(input.names)) {
    if (IMAGEPASTE_BASENAME_RE.test(name)) {
      toSign.push(name);
    } else {
      out.set(name, null);
    }
  }

  for (let i = 0; i < toSign.length; i += IMAGEPASTE_URLS_BATCH_SIZE) {
    const batch = toSign.slice(i, i + IMAGEPASTE_URLS_BATCH_SIZE);
    try {
      const res = await fetchFn(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${input.adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ names: batch }),
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      });
      if (!res.ok) {
        logger.warn('resignImagepasteUrls: batch request failed', {
          status: res.status,
          count: batch.length,
        });
        for (const name of batch) out.set(name, null);
        continue;
      }
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        logger.warn('resignImagepasteUrls: batch response was not JSON', { count: batch.length });
        for (const name of batch) out.set(name, null);
        continue;
      }
      const urls =
        typeof body === 'object' && body !== null
          ? (body as Partial<ImagepasteUrlsResponse>).urls
          : undefined;
      for (const name of batch) {
        const signed = urls?.[name];
        out.set(name, typeof signed === 'string' ? signed : null);
      }
    } catch (cause) {
      logger.warn('resignImagepasteUrls: batch network error', {
        err: cause instanceof Error ? cause.message : String(cause),
        count: batch.length,
      });
      for (const name of batch) out.set(name, null);
    }
  }

  return out;
};
